
import { SPFI, spfi } from '@pnp/sp';
import { LogLevel, PnPLogging } from '@pnp/logging';
import '@pnp/sp/webs';
import '@pnp/sp/lists';
import '@pnp/sp/items';
import '@pnp/sp/batching';
import { BrowserFetchWithRetry } from "@pnp/queryable";
import LoggingService from '../LoggingService';

/**
 * Base class for PnPjs service
 */
export abstract class PnPjsServiceBase {
  protected sp: SPFI | null = null;
  protected siteUrl: string | null = null;
  protected _isInitialized = false;
  protected readonly LOG_SOURCE = 'PnPjsService';

  /**
   * Check if the service is initialized
   */
  public isInitialized(): boolean {
    return this._isInitialized;
  }

  /**
   * Get the current site URL
   */
  public getSiteUrl(): string | null {
    return this.siteUrl;
  }

  /**
   * Set the default site URL to use when no site URL is provided
   * @param siteUrl The default site URL
   */
  public setDefaultSiteUrl(siteUrl: string): void {
    if (!siteUrl) {
      LoggingService.warn('Attempted to set empty site URL', {}, this.LOG_SOURCE);
      return;
    }
    
    // Clean up the URL - remove trailing slashes
    let cleanUrl = siteUrl.trim();
    while (cleanUrl.endsWith('/')) {
      cleanUrl = cleanUrl.slice(0, -1);
    }
    
    // Validate URL format
    try {
      new URL(cleanUrl);
    } catch (e) {
      LoggingService.error('Invalid site URL format', { siteUrl }, this.LOG_SOURCE);
      return;
    }

    LoggingService.info('Setting default site URL', { siteUrl: cleanUrl, previous: this.siteUrl }, this.LOG_SOURCE);
    this.siteUrl = cleanUrl;
    
    // Update the SP instance with the new site URL if it exists
    if (this.sp) {
      const sp = spfi(cleanUrl);
      sp.using(BrowserFetchWithRetry());
      this.sp = sp;
      LoggingService.debug('Updated SP instance with new site URL and browser fetch client', { siteUrl: cleanUrl }, this.LOG_SOURCE);
    }
  }

  /**
   * Create an SP instance for the provided site URL
   * @param targetSiteUrl The site URL to use
   */
  protected createSPInstance(targetSiteUrl: string): SPFI {
    try {
      // Clean the URL
      let cleanUrl = targetSiteUrl.trim();
      while (cleanUrl.endsWith('/')) {
        cleanUrl = cleanUrl.slice(0, -1);
      }
      
      LoggingService.debug('Creating SP instance for URL', { url: cleanUrl }, this.LOG_SOURCE);
      const sp = spfi(cleanUrl);
      sp.using(BrowserFetchWithRetry());
      return sp;
    } catch (error) {
      LoggingService.error('Error creating SP instance', error, this.LOG_SOURCE);
      throw error;
    }
  }
}
